import type { PermissionKey } from '@classroom-ai/types';

import { useMemo } from 'react';
import { useAuthContext } from '@classroom-ai/ui/auth/hooks';
import { useGetMyPermissions } from '@dataconnect/generated/react';

import { useLeadScope } from 'src/class/use-lead-scope';
import { useClassAccess, useActiveCourse } from 'src/class/class-provider';

// ----------------------------------------------------------------------

/** Tooltip shown on a control disabled because the user lacks the required permission. */
export const PERMISSION_DENIED_MESSAGE =
  "You don't manage this course. Ask your school admin for Lead access.";

/** Tooltip on a class control while viewing a colleague's class (epic #566). */
export function viewOnlyMessage(teacherName: string): string {
  return `This is ${teacherName}'s class — view only.`;
}

/** Keys that act on course-level data: they hold only in a course the user manages (epic #566). */
const COURSE_KEYS = new Set<string>([
  'course:manage', 'curriculum:upload', 'curriculum:generate_seeds', 'lesson:manage', 'lesson:generate_ai',
]);
/** Keys that change a class: they hold only in your own class, never one you're viewing. */
const CLASS_KEYS = new Set<string>([
  'pacing:manage_class', 'class:configure_mastery',
  // Student records (epic #595): a class teacher edits their own students only.
  'student:manage_profile', 'student:manage_demographics', 'guardian:manage',
]);

// ----------------------------------------------------------------------

export type UsePermissionsResult = {
  /**
   * Whether the current user holds `key` where they're acting (epic #566): the key from their
   * permission groups in the active school year, AND for a course-level key (`course:manage`,
   * `lesson:*`, `curriculum:*`) a Lead access grant for the course — `scope.courseId`, else the
   * active course — AND for a class-level key (`pacing:manage_class`, `class:configure_mastery`)
   * not viewing a colleague's class. Mirrors the server's checks.
   */
  has: (key: PermissionKey, scope?: { courseId?: string }) => boolean;
  /** The raw permission-key set for the active school year (empty for ED360_STAFF — see `has`). */
  keys: ReadonlySet<string>;
  /** True until the answer is known. Treat loading as denied (fail safe). */
  loading: boolean;
  schoolYearId: string | null;
};

// ----------------------------------------------------------------------

/**
 * The current teacher's effective permissions in the school year they're acting in.
 *
 * Bare hook, no provider — the generated Data Connect hooks are TanStack Query, so N call
 * sites dedupe through the query cache. Scopes off `activeSchoolYearId` from
 * `useActiveCourse()` (the year of the active course/class), which resolves from school-year
 * membership too — so a class-less Department Head gets real permissions (epic #143 Risk #2).
 *
 * Two load-bearing rules:
 *  - Gate on `isPending`, never `isFetching`: `invalidateAll` fires an unfiltered
 *    `invalidateQueries()` after every write, so `isFetching` would flash every gated control
 *    off→on on each save. `isPending` stays false while cached data exists.
 *  - Fold the provider's loading in: at boot there's no school year, so the query is
 *    disabled and its `isPending` is false — without the provider term the hook would report
 *    a false "denied" (empty keys, not loading) for a beat, then flip granted.
 *
 * ED360_STAFF bypass is correctness, not optimization: a super-admin has no
 * `SchoolYearUserGroup` rows, so the query would legitimately return empty and deny them.
 */
export function usePermissions(): UsePermissionsResult {
  const { user } = useAuthContext();
  const isEd360 = user?.role === 'ED360_STAFF';

  const { activeSchoolYearId, activeCourseId, isLoading: contextLoading } = useActiveCourse();
  const { viewing } = useClassAccess();
  const schoolYearId = activeSchoolYearId;
  const { scope: lead, loading: leadLoading } = useLeadScope(isEd360 ? null : schoolYearId);

  const q = useGetMyPermissions(
    { schoolYearId: schoolYearId ?? '' },
    { enabled: !isEd360 && !!schoolYearId }
  );

  const keys = useMemo<ReadonlySet<string>>(() => {
    const set = new Set<string>();
    for (const g of q.data?.schoolYearUserGroups ?? []) {
      for (const item of g.group.items) set.add(item.permission.key);
    }
    return set;
  }, [q.data]);

  const has = useMemo(() => {
    const managed = new Set(lead.managedCourseIds.map(norm));
    return (key: PermissionKey, scope?: { courseId?: string }) => {
      if (isEd360) return true;
      if (!keys.has(key)) return false;
      if (COURSE_KEYS.has(key)) {
        const courseId = scope?.courseId ?? activeCourseId;
        if (!courseId || !managed.has(norm(courseId))) return false;
      }
      if (CLASS_KEYS.has(key) && viewing) return false;
      return true;
    };
  }, [keys, isEd360, lead.managedCourseIds, activeCourseId, viewing]);

  // ED360_STAFF never waits (query disabled). Otherwise: loading while the provider is still
  // resolving a school year, or (once there is one) until the query's first result. We only
  // consult `q.isPending` when the query is actually enabled — a disabled TanStack query is
  // perpetually `isPending`, so a user with genuinely no school year must resolve to denied,
  // not spin forever. `isPending` (not `isFetching`) so post-write invalidation doesn't flash
  // controls off→on on every save.
  const loading = isEd360 ? false : contextLoading || leadLoading || (!!schoolYearId && q.isPending);

  return { has, keys, loading, schoolYearId };
}

const norm = (id: string) => id.replace(/-/g, '').toLowerCase();
